import { CaseDto } from '@app/shared/models/contracts/project-dto';
import { InstrumentDefinitionWithId } from '@app/core/store/capital-structure-slice-selectors';
import { Pwerm2CalculationResultsDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { getInstrumentTransactions } from '@app/modules/projects/outputs/pwerm2-summary-waterfall/valuation/utils/transactionUtils';
import { enumKeyByValue } from '@app/shared/helpers';
import { InstrumentType, TrancheKey } from '@app/shared/models/contracts/enums/shared-enums';
import styles from './discount-rate-override.module.scss';
import { OverrideField } from './useDiscountRateOverride';
import {
  getWaterfallCaseYearInstrumentInfusionTransactionSummary,
  getWaterfallCaseYearInstrumentTransactionSummary,
  getInstrumentCaseYearTranche,
  getInstrumentCaseYearFirstTranche,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters';

export interface CaseStructureItem {
  caseId: string;
  narrative?: string;
  forecasts: { forecastId: string; forecastYear: string }[];
}

export const getInstrumentOverrideIndex = (
  initializedCases: CaseDto[],
  caseIndex: number,
  forecastIndex: number,
  instrumentId: string
): number => {
  const forecast = initializedCases[caseIndex]?.forecasts[forecastIndex];
  if (!forecast?.discountRateOverrides) {
    return -1;
  }
  return forecast.discountRateOverrides.findIndex((o) => o.instrumentId === instrumentId);
};

export const isInstrumentOverridden = (
  cases: CaseDto[],
  instrumentId: string,
  hasInfusions = false,
  hasOtherInterimCashflows = false,
  trancheIds: string[] = []
): boolean =>
  (hasInfusions && fieldHasAnyOverrides(cases, instrumentId, 'infusionDiscountRateOverride')) ||
  (hasOtherInterimCashflows &&
    fieldHasAnyOverrides(cases, instrumentId, 'otherInterimsDiscountRateOverride')) ||
  trancheIds.some((trancheId) =>
    fieldHasAnyOverrides(
      cases,
      instrumentId,
      'exitDistributionByTrancheDiscountRateOverrides',
      trancheId
    )
  );

export const fieldHasAnyOverrides = (
  cases: CaseDto[],
  instrumentId: string,
  field: OverrideField,
  trancheId: Nullable<string> = null
) => GetDisctountRateOverridesForField(cases, field, instrumentId, trancheId).length > 0;

const GetDisctountRateOverridesForField = (
  cases: CaseDto[],
  field: OverrideField,
  instrumentId: string,
  trancheId?: Nullable<string>
) => {
  if (field === 'exitDistributionByTrancheDiscountRateOverrides') {
    return cases.flatMap((caseItem) =>
      caseItem.forecasts.flatMap((forecast) =>
        (forecast.discountRateOverrides ?? []).filter(
          (override) =>
            override.instrumentId === instrumentId &&
            override[field][trancheId ?? TrancheKey.DefaultTrancheKey] != null
        )
      )
    );
  }

  return cases.flatMap((caseItem) =>
    caseItem.forecasts.flatMap((forecast) =>
      (forecast.discountRateOverrides ?? []).filter(
        (override) => override.instrumentId === instrumentId && override[field] != null
      )
    )
  );
};

export const getCellOverrideClass = (
  initializedCases: CaseDto[],
  caseIndex: number,
  forecastIndex: number,
  instrumentId: string,
  trancheId: Nullable<string>,
  field: OverrideField
): string => {
  const instrumentIdx = getInstrumentOverrideIndex(
    initializedCases,
    caseIndex,
    forecastIndex,
    instrumentId
  );
  if (instrumentIdx === -1) {
    return '';
  }
  const instrumentEntry =
    initializedCases[caseIndex].forecasts[forecastIndex].discountRateOverrides[instrumentIdx];
  if (field === 'exitDistributionByTrancheDiscountRateOverrides') {
    const exitKey = trancheId ?? TrancheKey.DefaultTrancheKey;
    return instrumentEntry.exitDistributionByTrancheDiscountRateOverrides[exitKey] != null
      ? styles['revertable-value']
      : '';
  }
  return instrumentEntry[field] !== null ? styles['revertable-value'] : '';
};

export const instrumentHasTransactions = (
  results: Pwerm2CalculationResultsDto,
  instrumentId: string
) => {
  const instrumentTransactions = getInstrumentTransactions(results, instrumentId);
  const hasInfusions = instrumentTransactions.containsInfusions;
  const hasOtherInterimCashflows =
    instrumentTransactions.containsRedemptions ||
    instrumentTransactions.containsCashPaid ||
    instrumentTransactions.containsDividends;
  return { hasInfusions, hasOtherInterimCashflows };
};

export const instrumentHasTranches = (instrument: InstrumentDefinitionWithId) => {
  const isOrdinaryEquity =
    instrument.type === enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity);
  return isOrdinaryEquity && Boolean(instrument.payoutLogic?.length);
};

export const getOriginalValue = (
  caseId: string,
  forecastId: string,
  field: OverrideField,
  instrumentId: string,
  results: Pwerm2CalculationResultsDto,
  trancheId?: Nullable<string>
): number | null | undefined => {
  switch (field) {
    case 'infusionDiscountRateOverride':
      return getCalculatedProjectValueInPercent(
        getWaterfallCaseYearInstrumentInfusionTransactionSummary(
          results,
          caseId,
          forecastId,
          instrumentId,
          (ts) => ts.originalInterimTransactionsDiscountRate
        )
      );
    case 'otherInterimsDiscountRateOverride':
      return getCalculatedProjectValueInPercent(
        getWaterfallCaseYearInstrumentTransactionSummary(
          results,
          caseId,
          forecastId,
          instrumentId,
          (ts) => ts.originalInterimTransactionsDiscountRate
        )
      );
    case 'exitDistributionByTrancheDiscountRateOverrides':
      if (trancheId) {
        return getCalculatedProjectValueInPercent(
          getInstrumentCaseYearTranche(
            results,
            instrumentId,
            caseId,
            forecastId,
            trancheId,
            (tranche) => tranche?.cumulativeCostOfEquity
          )
        );
      }
      return getCalculatedProjectValueInPercent(
        getInstrumentCaseYearFirstTranche(
          results,
          instrumentId,
          caseId,
          forecastId,
          (tranche) => tranche.cumulativeCostOfEquity
        )
      );
    default:
      return null;
  }
};
